import React, { useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  pointerWithin
} from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import {
  isProjectDraggingAtom,
  draggedProjectIdAtom
} from '../../../../atoms/v3/taskAtoms';
import { foldersAtom } from '../../../../atoms/v3/folderAtoms';
import { projectsAtom, updateProjectAtom } from '../../../../atoms/v0/projectAtoms';
import { isTouchDevice } from '../../../../utils/platformUtils';
import { Project } from '../../../../types/project';

interface ProjectDndContextProps {
  children: React.ReactNode;
}

/**
 * 项目拖放上下文组件
 * 提供项目在文件夹间拖拽的功能
 */
export const ProjectDndContext: React.FC<ProjectDndContextProps> = ({
  children
}) => {
  // 活动区域状态
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [activeFolderName, setActiveFolderName] = useState<string | null>(null);
  
  // 拖拽中的项目
  const [draggedProject, setDraggedProject] = useState<Project | null>(null);

  // Atoms
  const [folders] = useAtom(foldersAtom);
  const [projects] = useAtom(projectsAtom);
  const [, updateProject] = useAtom(updateProjectAtom);
  const [isProjectDragging, setIsProjectDragging] = useAtom(isProjectDraggingAtom);
  const [draggedProjectId, setDraggedProjectId] = useAtom(draggedProjectIdAtom);

  // 检测是否为触摸设备
  const isTouch = isTouchDevice();

  // 配置传感器 - 根据设备类型使用不同的传感器
  const sensors = useSensors(
    // 根据设备类型选择合适的传感器
    ...(isTouch ? [
      // 触摸设备：只使用 TouchSensor
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 250, // 250ms 长按延迟
          tolerance: 5, // 5px 移动容差
        },
      }),
      useSensor(KeyboardSensor),
    ] : [
      // 桌面端：使用 MouseSensor
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8, // 8px 距离激活
        },
      }),
      useSensor(KeyboardSensor),
    ])
  );

  // 重置拖拽状态
  const resetDragState = useCallback(() => {
    setIsProjectDragging(false);
    setDraggedProjectId(null);
    setDraggedProject(null);
    setActiveFolderId(null);
    setActiveFolderName(null);
  }, [setIsProjectDragging, setDraggedProjectId]);

  // 处理拖拽开始
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    
    if (active.data.current?.type === 'project-item') {
      const projectId = active.data.current.projectId;
      const project = active.data.current.project;
      
      setIsProjectDragging(true);
      setDraggedProjectId(projectId);
      setDraggedProject(project);
      
      console.log('ProjectDndContext: 开始拖拽项目:', projectId);
    }
  };

  // 处理拖拽悬停
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (active.data.current?.type === 'project-item') {
      // 如果没有悬停在任何区域上，重置状态
      if (!over || !over.data.current) {
        if (activeFolderId !== null) {
          setActiveFolderId(null);
          setActiveFolderName(null);
        }
        return;
      }

      // 获取当前悬停区域的类型和数据
      const areaData = over.data.current;
      const areaType = areaData.type;

      if (areaType === 'folder') {
        // 文件夹区域
        const currentFolderId = areaData.folderId;

        if (currentFolderId !== activeFolderId) {
          setActiveFolderId(currentFolderId);

          // 查找文件夹名称
          if (currentFolderId === 'ungrouped') {
            setActiveFolderName('未分组');
          } else {
            const folder = folders.find(f => f.id === currentFolderId);
            const folderName = folder ? folder.name : '未知文件夹';
            setActiveFolderName(folderName);
          }

          console.log('ProjectDndContext: 悬停在文件夹:', currentFolderId, activeFolderName);
        }
      }
    }
  };

  // 处理拖拽结束
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.data.current?.type === 'project-item' && over && over.data.current) {
      const projectId = active.data.current.projectId;
      const currentFolderId = active.data.current.currentFolderId;
      const areaData = over.data.current;

      if (areaData.type === 'folder') {
        const targetFolderId = areaData.folderId;
        
        // 如果目标文件夹与当前文件夹不同，则更新项目
        if (targetFolderId !== currentFolderId) {
          console.log('ProjectDndContext: 移动项目到文件夹:', projectId, '->', targetFolderId);
          
          try {
            // 准备更新数据
            const updateData = {
              id: projectId,
              folderId: targetFolderId === 'ungrouped' ? null : targetFolderId
            };

            // 找到当前项目的完整信息
            const project = projects.find(p => p.id === projectId);
            if (project) {
              updateData.title = project.title;
              updateData.context = project.context;
            }

            // 更新项目
            await updateProject(updateData);
            console.log('ProjectDndContext: 项目移动成功');
          } catch (error) {
            console.error('ProjectDndContext: 移动项目失败:', error);
          }
        }
      }
    }

    // 重置所有状态
    resetDragState();
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      {children}

      {/* 拖拽覆盖层 - 显示正在拖拽的项目 */}
      <DragOverlay>
        {isProjectDragging && draggedProject && (
          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark max-w-md px-3 py-2">
            <div className="text-ios-body text-ios-gray dark:text-ios-dark-gray truncate">
              {draggedProject.title}
            </div>
          </div>
        )}
      </DragOverlay>

      {/* 拖拽提示 */}
      <AnimatePresence>
        {isProjectDragging && activeFolderName && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 bg-ios-blue dark:bg-ios-dark-blue text-white px-4 py-2 rounded-full text-ios-caption font-medium shadow-lg"
          >
            移动到"{activeFolderName}"
          </motion.div>
        )}
      </AnimatePresence>
    </DndContext>
  );
};
